/**
 * Shared DTOs and Zod schemas for the budget route.
 *
 * The budget is the broad-level monthly plan — what the month is
 * *supposed* to look like — not a transaction ledger (MoneyWiz is
 * canonical for day-to-day capture; see PROJECT.md §Almanac is a hub).
 * Each month is a self-contained snapshot: income, expense and savings
 * groups of items with a planned and an actual figure, plus annual
 * bills provisioned into their group at ÷12. Editing one month never
 * touches another; a new month starts by carrying the previous plan
 * forward with blank actuals, or from scratch.
 *
 * The whole snapshot travels as one JSON document (`budget_months.data`)
 * because edits are single-user, low-frequency and always whole-month:
 * per-item rows would buy nothing but join complexity. The same Zod
 * schema validates it on the API and types it on the client.
 */

import { z } from 'zod';

/** Month key: 'YYYY-MM'. The one addressing unit for budget data. */
export const BUDGET_MONTH_KEY_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
export const budgetMonthKeySchema = z.string().regex(BUDGET_MONTH_KEY_RE);

// Amounts are plain numbers in the user's display currency. Bounded
// so a fat-fingered paste can't persist something absurd; two decimal
// places are not enforced — rounding is a display concern.
const amountSchema = z.number().finite().min(0).max(100_000_000);

export const budgetItemSchema = z.object({
  id: z.string().min(1).max(64),
  label: z.string().min(1).max(120),
  /** What the plan says this line should be per month. */
  planned: amountSchema,
  /** What it actually was this month. 0 = nothing entered yet. */
  actual: amountSchema,
});
export type BudgetItem = z.infer<typeof budgetItemSchema>;

/**
 * Group kinds drive both the arithmetic and the rendering:
 *  - 'income'  — counts toward income totals; diff is actual − planned.
 *  - 'expense' — counts toward committed; diff is planned − actual.
 *  - 'savings' — counts toward committed too (money deliberately put
 *    away is spoken for), but renders as progress bars and feeds the
 *    savings rate.
 */
export const budgetGroupKinds = ['income', 'expense', 'savings'] as const;
export type BudgetGroupKind = (typeof budgetGroupKinds)[number];

export const budgetGroupSchema = z.object({
  id: z.string().min(1).max(64),
  title: z.string().min(1).max(120),
  kind: z.enum(budgetGroupKinds),
  /** Expense groups only: true = fixed costs the Balancer must never
   *  trim (rent, insurance). Absent/false = flexible. Optional so
   *  pre-Balancer snapshots keep parsing unchanged. */
  fixed: z.boolean().optional(),
  items: z.array(budgetItemSchema).max(100),
});
export type BudgetGroup = z.infer<typeof budgetGroupSchema>;

/**
 * A yearly bill (car insurance, TV licence…) provisioned monthly:
 * annualAmount ÷ 12 appears as a read-only row in `groupId`'s card
 * and in every total, so the month the bill lands isn't a surprise.
 */
export const budgetAnnualBillSchema = z.object({
  id: z.string().min(1).max(64),
  label: z.string().min(1).max(120),
  annualAmount: amountSchema,
  groupId: z.string().min(1).max(64),
});
export type BudgetAnnualBill = z.infer<typeof budgetAnnualBillSchema>;

export const budgetSnapshotSchema = z.object({
  groups: z.array(budgetGroupSchema).max(40),
  annualBills: z.array(budgetAnnualBillSchema).max(60),
});
export type BudgetSnapshot = z.infer<typeof budgetSnapshotSchema>;

/** One saved month, as `GET /api/almanac/budget/months` returns it. */
export interface BudgetMonthDTO {
  month: string; // 'YYYY-MM'
  data: BudgetSnapshot;
  updatedAt: string;
}

export function emptyBudgetSnapshot(): BudgetSnapshot {
  return { groups: [], annualBills: [] };
}

/**
 * Display currency (users.currency). Any ISO 4217 code is accepted on
 * the wire — the Settings selector offers the curated list below, but
 * a code we didn't think of shouldn't be a 400.
 */
export const currencySchema = z.string().regex(/^[A-Z]{3}$/);
export const DEFAULT_CURRENCY = 'CHF';

/** Choices for the Settings selector. Order = display order. */
export const currencyChoices = ['CHF', 'EUR', 'USD', 'GBP', 'AUD', 'RON'] as const;
